import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField,
  Button, Chip, CircularProgress, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  Search, Thermostat, Air, Opacity, Visibility,
  WbSunny, Cloud, Grain, Thunderstorm, AcUnit,
  LocationOn, Refresh, TrendingUp, AutoAwesome,
  Fullscreen, FullscreenExit,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, Filler);

// Open-Meteo API — completely free, no API key needed
const GEOCODING_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const WEATHER_URL   = 'https://api.open-meteo.com/v1/forecast';

const WMO_CODES = {
  0:  { label: 'Clear Sky',        icon: '☀️' },
  1:  { label: 'Mainly Clear',     icon: '🌤️' },
  2:  { label: 'Partly Cloudy',    icon: '⛅' },
  3:  { label: 'Overcast',         icon: '☁️' },
  45: { label: 'Foggy',            icon: '🌫️' },
  48: { label: 'Icy Fog',          icon: '🌫️' },
  51: { label: 'Light Drizzle',    icon: '🌦️' },
  61: { label: 'Light Rain',       icon: '🌧️' },
  63: { label: 'Moderate Rain',    icon: '🌧️' },
  65: { label: 'Heavy Rain',       icon: '🌧️' },
  71: { label: 'Light Snow',       icon: '🌨️' },
  73: { label: 'Moderate Snow',    icon: '❄️' },
  75: { label: 'Heavy Snow',       icon: '❄️' },
  80: { label: 'Rain Showers',     icon: '🌦️' },
  95: { label: 'Thunderstorm',     icon: '⛈️' },
  99: { label: 'Heavy Thunderstorm', icon: '⛈️' },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function EarthWeatherPage() {
  const [city, setCity]           = useState('');
  const [weather, setWeather]     = useState(null);
  const [forecast, setForecast]   = useState(null);
  const [hourly, setHourly]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [locationName, setLocationName] = useState('');
  const [aiPrediction, setAiPrediction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [coords, setCoords] = useState({ lat: 10.100, lon: 97.170, zoom: 3 });

  // Auto-load user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => fetchWeatherByCoords(pos.coords.latitude, pos.coords.longitude, 'Your Location'),
        ()  => fetchWeatherByCity('Mumbai')
      );
    } else {
      fetchWeatherByCity('Mumbai');
    }
  }, []);

  const fetchWeatherByCity = async (cityName) => {
    setLoading(true);
    setError('');
    try {
      // Step 1: Geocode city name
      const geoRes  = await fetch(`${GEOCODING_URL}?name=${encodeURIComponent(cityName)}&count=1`);
      const geoData = await geoRes.json();

      if (!geoData.results?.length) {
        setError(`City "${cityName}" not found. Try another name.`);
        return;
      }

      const { latitude, longitude, name, country } = geoData.results[0];
      await fetchWeatherByCoords(latitude, longitude, `${name}, ${country}`);
    } catch (err) {
      setError('Failed to fetch weather data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeatherByCoords = async (lat, lon, name) => {
    setLoading(true);
    setError('');
    setLocationName(name);
    setCoords({ lat, lon, zoom: 5 });
    try {
      const res = await fetch(
        `${WEATHER_URL}?latitude=${lat}&longitude=${lon}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,` +
        `precipitation,weather_code,wind_speed_10m,wind_direction_10m,` +
        `surface_pressure,visibility,uv_index` +
        `&hourly=temperature_2m,precipitation_probability,weather_code` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,` +
        `precipitation_sum,wind_speed_10m_max,sunrise,sunset,uv_index_max` +
        `&timezone=auto&forecast_days=7`
      );
      const data = await res.json();

      setWeather(data.current);
      setForecast(data.daily);
      setHourly(data.hourly);
      setAiPrediction('');
    } catch (err) {
      setError('Weather API error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAIPrediction = async () => {
    if (!weather || !forecast) return;
    setAiLoading(true);
    try {
      const prompt = `Based on this weather data for ${locationName}:
Current: ${weather.temperature_2m}°C, ${WMO_CODES[weather.weather_code]?.label || 'Unknown'}, 
Wind: ${weather.wind_speed_10m} km/h, Humidity: ${weather.relative_humidity_2m}%,
UV Index: ${weather.uv_index}, Pressure: ${weather.surface_pressure} hPa

7-day forecast max temps: ${forecast.temperature_2m_max?.slice(0,7).join(', ')}°C
7-day forecast min temps: ${forecast.temperature_2m_min?.slice(0,7).join(', ')}°C
7-day precipitation: ${forecast.precipitation_sum?.slice(0,7).join(', ')} mm

Please provide:
1. A brief weather analysis and what to expect
2. Key weather patterns or trends you notice
3. Practical advice for the week (clothing, activities, travel)
4. Any weather warnings or concerns
5. Best and worst days this week

Keep response concise and practical.`;

      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${backendUrl}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      const data = await response.json();
      setAiPrediction(data.content?.[0]?.text || 'Could not generate prediction.');
    } catch (err) {
      setAiPrediction('AI prediction unavailable.');
    } finally {
      setAiLoading(false);
    }
  };

  const wmo = weather ? (WMO_CODES[weather.weather_code] || { label: 'Unknown', icon: '🌡️' }) : null;

  // Hourly chart data (next 24 hours)
  const hourlyChartData = hourly ? {
    labels: hourly.time?.slice(0, 24).map(t => t.slice(11, 16)),
    datasets: [
      {
        label: 'Temperature (°C)',
        data: hourly.temperature_2m?.slice(0, 24),
        borderColor: '#ffa726',
        backgroundColor: 'rgba(255,167,38,0.1)',
        fill: true, tension: 0.4, pointRadius: 2,
      },
      {
        label: 'Rain Chance (%)',
        data: hourly.precipitation_probability?.slice(0, 24),
        borderColor: '#ffffff',
        backgroundColor: 'rgba(255,255,255,0.05)',
        fill: true, tension: 0.4, pointRadius: 2,
        yAxisID: 'y1',
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { color: '#90caf9', font: { size: 11 }, boxWidth: 12 } },
      tooltip: { backgroundColor: 'rgba(10,22,40,0.95)', borderColor: 'rgba(255,255,255,0.3)',
        borderWidth: 1, titleColor: '#e3f2fd', bodyColor: '#90caf9', padding: 10, cornerRadius: 8 },
    },
    scales: {
      x:  { ticks: { color: '#546e7a', font: { size: 10 }, maxTicksLimit: 8 }, grid: { color: 'rgba(255,255,255,0.06)' } },
      y:  { ticks: { color: '#546e7a', font: { size: 11 }, callback: v => `${v}°` }, grid: { color: 'rgba(255,255,255,0.06)' } },
      y1: { position: 'right', ticks: { color: '#ffffff', callback: v => `${v}%` }, grid: { display: false }, min: 0, max: 100 },
    },
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>🌍 Earth Weather</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Real-time weather + AI predictions • Powered by Open-Meteo
          </Typography>
        </Box>
        {weather && (
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchWeatherByCity(city || locationName)} sx={{ color: 'primary.main' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth size="small" placeholder="Search any city... (e.g. Mumbai, Tokyo, New York)"
              value={city} onChange={e => setCity(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchWeatherByCity(city)}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} /> }}
            />
            <Button variant="contained" onClick={() => fetchWeatherByCity(city)}
              disabled={!city.trim() || loading} sx={{ px: 3, whiteSpace: 'nowrap' }}>
              {loading ? <CircularProgress size={18} color="inherit" /> : 'Search'}
            </Button>
          </Box>
          {error && (
            <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>{error}</Typography>
          )}
        </CardContent>
      </Card>

      {loading && !weather && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {weather && (
        <>
          {/* Current Weather */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Main current card */}
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, rgba(2,136,209,0.1), rgba(5,13,26,0.9))' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOn sx={{ color: 'primary.main', fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ color: 'primary.light', fontWeight: 700 }}>
                      {locationName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ fontSize: 72, lineHeight: 1, fontWeight: 800, color: 'text.primary' }}>
                        {Math.round(weather.temperature_2m)}°
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {wmo?.icon} {wmo?.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Feels like {Math.round(weather.apparent_temperature)}°C
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                  <Grid container spacing={1.5}>
                    {[
                      { icon: <Opacity sx={{ fontSize: 16 }} />, label: 'Humidity', value: `${weather.relative_humidity_2m}%` },
                      { icon: <Air sx={{ fontSize: 16 }} />,     label: 'Wind',     value: `${weather.wind_speed_10m} km/h` },
                      { icon: <Visibility sx={{ fontSize: 16 }} />, label: 'Visibility', value: `${(weather.visibility / 1000).toFixed(1)} km` },
                      { icon: <WbSunny sx={{ fontSize: 16 }} />, label: 'UV Index', value: weather.uv_index },
                      { icon: <Thermostat sx={{ fontSize: 16 }} />, label: 'Pressure', value: `${weather.surface_pressure} hPa` },
                      { icon: <Grain sx={{ fontSize: 16 }} />,   label: 'Rain',     value: `${weather.precipitation} mm` },
                    ].map(({ icon, label, value }) => (
                      <Grid item xs={4} key={label}>
                        <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Box sx={{ color: 'primary.main', mb: 0.3 }}>{icon}</Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 9 }}>
                            {label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 11 }}>
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* 7-day forecast */}
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>7-Day Forecast</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {forecast?.time?.slice(0, 7).map((date, i) => {
                      const dayWmo  = WMO_CODES[forecast.weather_code[i]] || { icon: '🌡️', label: '' };
                      const dayName = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : DAYS[new Date(date).getDay()];
                      const maxT    = Math.round(forecast.temperature_2m_max[i]);
                      const minT    = Math.round(forecast.temperature_2m_min[i]);
                      const rain    = forecast.precipitation_sum[i];

                      return (
                        <Box key={date} sx={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          p: 1, borderRadius: 1,
                          bgcolor: i === 0 ? 'rgba(255,255,255,0.08)' : 'transparent',
                          border: i === 0 ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent',
                        }}>
                          <Typography variant="body2" sx={{ minWidth: 80, fontWeight: i === 0 ? 700 : 400, color: 'text.primary' }}>
                            {dayName}
                          </Typography>
                          <Typography sx={{ fontSize: 20, minWidth: 32 }}>{dayWmo.icon}</Typography>
                          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
                            {dayWmo.label}
                          </Typography>
                          {rain > 0 && (
                            <Chip label={`${rain}mm`} size="small"
                              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', fontSize: 9, height: 18 }} />
                          )}
                          <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'right', color: 'text.secondary' }}>
                            <span style={{ color: '#ffa726', fontWeight: 700 }}>{maxT}°</span>
                            {' / '}
                            <span style={{ color: '#90caf9' }}>{minT}°</span>
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Hourly chart */}
          {hourlyChartData && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  24-Hour Temperature & Rain Chance
                </Typography>
                <Box sx={{ height: 200 }}>
                  <Line data={hourlyChartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* AI Prediction */}
          <Card sx={{ border: '1px solid rgba(255,255,255,0.2)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TrendingUp sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    AI Weather Analysis & Prediction
                  </Typography>
                  <Chip label="Powered by Claude AI" size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light',
                      border: '1px solid rgba(255,255,255,0.2)', fontSize: 10, fontWeight: 700 }} />
                </Box>
                <Button variant="outlined" size="small" onClick={getAIPrediction}
                  disabled={aiLoading}
                  startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesome />}
                  sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'primary.light' }}>
                  {aiLoading ? 'Analyzing...' : aiPrediction ? 'Refresh Analysis' : 'Get AI Analysis'}
                </Button>
              </Box>

              {!aiPrediction && !aiLoading && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <TrendingUp sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">
                    Click "Get AI Analysis" for intelligent weather insights and predictions
                  </Typography>
                </Box>
              )}

              {aiPrediction && (
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>
                  {aiPrediction}
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Windy Live Weather Map ──────────────────────────────────────────── */}
      <WindyMap coords={coords} />
    </Box>
  );
}

const WINDY_LAYERS = [
  { key: 'satellite', label: 'Satellite',        emoji: '🛰️',  color: '#00b4ff', product: 'satellite' },
  { key: 'radar',     label: 'Weather Radar',    emoji: '📡',  color: '#ff6b35', product: 'radar'     },
  { key: 'wind',      label: 'Wind',             emoji: '💨',  color: '#00e5ff', product: 'ecmwf'     },
  { key: 'rain',      label: 'Rain & Thunder',   emoji: '🌧️',  color: '#4fc3f7', product: 'ecmwf'     },
  { key: 'temp',      label: 'Temperature',      emoji: '🌡️',  color: '#ff7043', product: 'ecmwf'     },
  { key: 'hurricane', label: 'Hurricane Tracker',emoji: '🌀',  color: '#e040fb', product: 'ecmwf'     },
  { key: 'clouds',    label: 'Clouds',           emoji: '☁️',  color: '#b0bec5', product: 'ecmwf'     },
  { key: 'waves',     label: 'Waves',            emoji: '🌊',  color: '#26c6da', product: 'ecmwf'     },
  { key: 'rainAccu',  label: 'Rain Accumulation',emoji: '🌂',  color: '#42a5f5', product: 'ecmwf'     },
  { key: 'thunder',   label: 'Thunderstorms',    emoji: '⛈️',  color: '#ffca28', product: 'ecmwf'     },
];

function WindyMap({ coords }) {
  const [activeLayer, setActiveLayer] = useState('satellite');
  const [fullscreen, setFullscreen]   = useState(false);
  const mapRef = useRef(null);

  const toggleFullscreen = async () => {
    if (!fullscreen) {
      await mapRef.current?.requestFullscreen?.();
      // Lock to landscape on mobile
      try { await window.screen.orientation?.lock?.('landscape'); } catch (_) {}
    } else {
      try { window.screen.orientation?.unlock?.(); } catch (_) {}
      document.exitFullscreen?.();
    }
    setFullscreen(f => !f);
  };

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        try { window.screen.orientation?.unlock?.(); } catch (_) {}
        setFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const layer = WINDY_LAYERS.find(l => l.key === activeLayer);
  const src = `https://embed.windy.com/embed2.html` +
    `?lat=${coords.lat}&lon=${coords.lon}` +
    `&detailLat=${coords.lat}&detailLon=${coords.lon}` +
    `&zoom=${coords.zoom}&level=surface` +
    `&overlay=${layer.key}&product=${layer.product}` +
    `&menu=&message=true&marker=&calendar=now` +
    `&pressure=&type=map&location=coordinates` +
    `&detail=&metricWind=default&metricTemp=default&radarRange=-1`;

  return (
    <Card sx={{ mt: 3, border: '1px solid rgba(255,255,255,0.12)', overflow: 'hidden' }}>
      <CardContent sx={{ p: 2.5, pb: '16px !important' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: 24 }}>🌍</Typography>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Live Weather Map
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Real-time satellite, radar & atmospheric data · Powered by Windy
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="ECMWF" size="small"
              sx={{ bgcolor: 'rgba(0,180,255,0.12)', color: '#00b4ff',
                border: '1px solid rgba(0,180,255,0.3)', fontSize: 10, fontWeight: 700 }} />
            <Tooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <IconButton onClick={toggleFullscreen} size="small"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
                }}>
                {fullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Layer switcher */}
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2,
          p: 1.5, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          {WINDY_LAYERS.map(l => (
            <Button
              key={l.key}
              size="small"
              onClick={() => setActiveLayer(l.key)}
              sx={{
                borderRadius: 2,
                px: 1.5, py: 0.6,
                fontSize: 11,
                fontWeight: activeLayer === l.key ? 700 : 500,
                bgcolor: activeLayer === l.key ? `${l.color}22` : 'transparent',
                color: activeLayer === l.key ? l.color : 'rgba(255,255,255,0.55)',
                border: `1px solid ${activeLayer === l.key ? l.color + '66' : 'rgba(255,255,255,0.1)'}`,
                textTransform: 'none',
                gap: 0.5,
                minWidth: 'unset',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: `${l.color}18`,
                  color: l.color,
                  border: `1px solid ${l.color}44`,
                },
              }}
            >
              <span style={{ fontSize: 14 }}>{l.emoji}</span>
              {l.label}
            </Button>
          ))}
        </Box>

        {/* Active layer badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: layer.color,
            boxShadow: `0 0 6px ${layer.color}`,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
          }} />
          <Typography sx={{ fontSize: 12, color: layer.color, fontWeight: 600 }}>
            {layer.emoji} {layer.label} — Live
          </Typography>
        </Box>

        {/* Map iframe */}
        <Box ref={mapRef} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', bgcolor: '#000' }}>
          <iframe
            key={`${coords.lat}-${coords.lon}-${activeLayer}`}
            title={`Windy ${layer.label}`}
            src={src}
            style={{ width: '100%', height: fullscreen ? '100vh' : 520, border: 'none', display: 'block' }}
            allow="geolocation fullscreen"
            loading="lazy"
          />
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
          Scroll to zoom · Click map for local details · Map centers on your searched location
        </Typography>
      </CardContent>
    </Card>
  );
}